import React, { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { getOrBuildCache } from "../../utils/chart/financial-cache.utils";
import {
  formatChartPeriodLabel,
  getChartPeriodData,
  resolveProjectEndChartMarker,
} from "../../utils/chart/chart-period.utils";
import ProjectEndReferenceLine from "./ProjectEndReferenceLine";
import type { Revenue, Expense } from "../../types/startup.types";
import { CHART_COLORS } from "@/constants/chart-colors";

const OPERATIONAL_COLORS = {
  revenue: CHART_COLORS.revenue,
  expenses: CHART_COLORS.expense,
} as const;

// Using main type definitions from startup.types.ts

interface EventData {
  id: string;
  date: Date;
  type: "funding" | "milestone" | "launch" | "asset" | "custom";
  priority: "high" | "medium" | "low";
  description: string;
  amount?: number;
  icon?: string;
  color?: string;
  // Asset-specific fields
  monthlyAppreciationRate?: number;
  minimumValue?: number;
}

interface ChartData {
  startDate: Date;
  endDate: Date;
  projectEndDate?: Date;
  initialBalance: number;
  expenses: Expense[];
  revenues?: Revenue[];
  events: EventData[];
}

interface CashFlowStatusProps {
  data: ChartData;
  visibleDateRange: {
    startDate: Date;
    endDate: Date;
  };
  height?: number;
}

const CashFlowStatusChart: React.FC<CashFlowStatusProps> = ({
  data,
  visibleDateRange,
  height = 300,
}) => {
  // Use the optimized cache for date range data - massive performance improvement!
  const { chartData, projectEndMarker } = useMemo(() => {
    const cache = getOrBuildCache(data);

    const rangeData = getChartPeriodData(
      cache,
      data as Parameters<typeof getChartPeriodData>[1],
      visibleDateRange.startDate,
      visibleDateRange.endDate,
    );

    const chartPoints = rangeData.map((periodData) => {
      const revenueTotal = Math.round(periodData.revenue + periodData.assetImpact);
      const expenseTotal = Math.round(periodData.expenses + periodData.oneTimeExpenses);

      return {
        month: formatChartPeriodLabel(periodData, "long"),
        revenue: revenueTotal,
        expenses: expenseTotal > 0 ? -expenseTotal : 0,
        expenseTotal,
        netOperationalFlow: Math.round(revenueTotal - expenseTotal),
        cumulativeCash: Math.round(periodData.cumulativeCash),
        date: new Date(periodData.month),
      };
    });

    const projectEndMarker = resolveProjectEndChartMarker(
      data.projectEndDate,
      visibleDateRange.startDate,
      visibleDateRange.endDate,
      rangeData.map((periodData) => ({
        label: formatChartPeriodLabel(periodData, "long"),
        periodStart: periodData.periodStart,
        periodEnd: periodData.periodEnd,
      })),
    );

    return { chartData: chartPoints, projectEndMarker };
  }, [data, visibleDateRange]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className="bg-surface-elevated text-white text-xs px-3 py-2 rounded-lg shadow-glow">
          <p className="font-semibold mb-2">{label}</p>
          <div className="space-y-1">
            <p className="text-brand">Revenue: ${Math.round(data.revenue).toLocaleString()}</p>
            <p className="text-expense">
              Expenses: ${Math.round(data.expenseTotal ?? Math.abs(data.expenses)).toLocaleString()}
            </p>
            <p className={`${data.netOperationalFlow >= 0 ? 'text-brand' : 'text-expense'}`}>
              Net Operational Flow: ${Math.round(data.netOperationalFlow).toLocaleString()}
            </p>
            <div className="border-t border-white/15 pt-1 mt-1">
              <p className={`font-bold text-white/55 text-xs`}>
                Total Cash Balance: ${Math.round(data.cumulativeCash).toLocaleString()}
              </p>
              <p className="text-xs text-white/40">(includes all funding)</p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="sh-panel overflow-hidden">
      <div className="border-b border-white/10 px-4 py-2 bg-surface-secondary/60">
        <h4 className="text-sm font-medium text-white/75">Operational Cash Flow ({chartData.length} months)</h4>
        <div className="flex items-center gap-4 mt-1 text-xs text-white/45">
          <div className="flex items-center gap-1">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: OPERATIONAL_COLORS.revenue }}
            />
            <span>Revenue (inflow)</span>
          </div>
          <div className="flex items-center gap-1">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: OPERATIONAL_COLORS.expenses }}
            />
            <span>Expenses (outflow)</span>
          </div>
        </div>
      </div>
      <div className="p-4">
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart
            data={chartData}
            margin={{
              top: 10,
              right: 30,
              left: 20,
              bottom: 10,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => {
                const absoluteValue = Math.abs(value);
                if (absoluteValue >= 1000000) {
                  return `$${(absoluteValue / 1000000).toFixed(1)}M`;
                } else if (absoluteValue >= 1000) {
                  return `$${(absoluteValue / 1000).toFixed(0)}K`;
                } else {
                  return `$${absoluteValue}`;
                }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" strokeWidth={1} />
            {projectEndMarker ? (
              <ProjectEndReferenceLine periodLabel={projectEndMarker.periodLabel} />
            ) : null}
            <Area
              type="monotone"
              dataKey="revenue"
              stroke={OPERATIONAL_COLORS.revenue}
              fill={OPERATIONAL_COLORS.revenue}
              fillOpacity={0.55}
              name="Revenue"
            />
            <Area
              type="monotone"
              dataKey="expenses"
              stroke={OPERATIONAL_COLORS.expenses}
              fill={OPERATIONAL_COLORS.expenses}
              fillOpacity={0.55}
              name="Expenses"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CashFlowStatusChart;
